import { useEffect, useRef } from 'react'
import type {
  CrmFunnelListSilentRefetchWsMessage,
  CrmRealtimeEventName,
  IntegrationChannelDeletedWsMessage,
  IntegrationMessageDeliveredWsMessage,
  IntegrationMessageDeletedWsMessage,
  IntegrationMessageInsertedWsMessage,
  IntegrationMessageNotDeliveredWsMessage,
  IntegrationMessageReadWsMessage,
  LeadUpdatedWsMessage,
  VkMessageDeliveredWsMessage,
  VkMessageInsertedWsMessage,
  VkMessageNotDeliveredWsMessage,
  VkMessageReadWsMessage,
} from '@/api/types/crm/crmRealtime.types'
/** Схлопывание пачки WS-событий перед refetch списка (мс). */
export const CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS = 500

/** Схлопывание WS-событий перед тихым refetch ленты VK-чата (мс). */
export const CRM_REALTIME_CHAT_REFETCH_DEBOUNCE_MS = 300

/** Пачка lead_updated от нескольких actions одного правила (мс). */
export const CRM_REALTIME_LEAD_UPDATED_DEBOUNCE_MS = 250

export const normalizeVkUnreadCount = (value: unknown): number => {
  const count = Number(value)
  return Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0
}

/** WS-события со `payload.status_ids` → refetch колонок канбана. */
export const normalizeCrmRealtimeStatusIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return []
  }

  const unique = new Set<number>()
  for (const item of value) {
    const statusId = Number(item)
    if (Number.isFinite(statusId) && statusId > 0) {
      unique.add(Math.floor(statusId))
    }
  }

  return [...unique]
}

/** @deprecated используйте normalizeCrmRealtimeStatusIds */
export const normalizeVkMessageInsertedStatusIds = normalizeCrmRealtimeStatusIds

const isCrmRealtimeWsMessageWithLeadId = (
  message: unknown,
  eventType: CrmRealtimeEventName,
): boolean => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as { type?: string; data?: { payload?: { lead_id?: unknown } } }
  if (typed.type !== eventType) {
    return false
  }

  const leadId = typed.data?.payload?.lead_id
  return Number.isFinite(Number(leadId))
}

export const isLeadCreatedWsMessage = (
  message: unknown,
): message is CrmFunnelListSilentRefetchWsMessage & { type: 'lead_created' } =>
  isCrmRealtimeWsMessageWithLeadId(message, 'lead_created')

export const isLeadDuplicateReceivedWsMessage = (
  message: unknown,
): message is CrmFunnelListSilentRefetchWsMessage & { type: 'lead_duplicate_received' } =>
  isCrmRealtimeWsMessageWithLeadId(message, 'lead_duplicate_received')

export const isLeadUpdatedWsMessage = (
  message: unknown,
): message is LeadUpdatedWsMessage => {
  if (!isCrmRealtimeWsMessageWithLeadId(message, 'lead_updated')) {
    return false
  }

  const typed = message as LeadUpdatedWsMessage
  const source = typed.data?.payload?.source
  // Пока source всегда automation; чужие source игнорируем заранее.
  if (source != null && String(source) !== 'automation') {
    return false
  }

  return true
}

export const isVkMessageInsertedWsMessage = (
  message: unknown,
): message is VkMessageInsertedWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as VkMessageInsertedWsMessage
  if (typed.type !== 'vk_message_inserted') {
    return false
  }

  const leadId = typed.data?.payload?.lead_id
  return Number.isFinite(Number(leadId))
}

export const isVkMessageReadWsMessage = (
  message: unknown,
): message is VkMessageReadWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as VkMessageReadWsMessage
  if (typed.type !== 'vk_message_read') {
    return false
  }

  const leadId = typed.data?.payload?.lead_id
  return Number.isFinite(Number(leadId))
}

export const isIntegrationMessageInsertedWsMessage = (
  message: unknown,
): message is IntegrationMessageInsertedWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as IntegrationMessageInsertedWsMessage
  if (typed.type !== 'integration_message_inserted') {
    return false
  }

  const payload = typed.data?.payload
  const conversationId = Number(payload?.conversation_id)
  const leadId = Number(payload?.lead_id)
  return (
    Number.isFinite(conversationId) &&
    conversationId > 0 &&
    Number.isFinite(leadId) &&
    leadId > 0
  )
}

export const isIntegrationMessageReadWsMessage = (
  message: unknown,
): message is IntegrationMessageReadWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as IntegrationMessageReadWsMessage
  if (typed.type !== 'integration_message_read') {
    return false
  }

  const payload = typed.data?.payload
  const conversationId = Number(payload?.conversation_id)
  const leadId = Number(payload?.lead_id)
  return (
    Number.isFinite(conversationId) &&
    conversationId > 0 &&
    Number.isFinite(leadId) &&
    leadId > 0
  )
}

const isIntegrationDeliveryWsPayloadForContext = (
  payload: {
    conversation_id?: unknown
    lead_id?: unknown
    provider?: unknown
  },
  context: {
    leadId: number
    conversationId: number | null
    provider?: string
  },
): boolean => {
  const eventLeadId = Number(payload.lead_id)
  if (Number.isFinite(eventLeadId) && eventLeadId > 0 && eventLeadId !== context.leadId) {
    return false
  }

  const eventConversationId = Number(payload.conversation_id)
  if (
    context.conversationId != null &&
    Number.isFinite(eventConversationId) &&
    eventConversationId > 0 &&
    eventConversationId !== context.conversationId
  ) {
    return false
  }

  if (
    context.provider &&
    typeof payload.provider === 'string' &&
    payload.provider !== context.provider
  ) {
    return false
  }

  return true
}

export const isIntegrationMessageDeliveredWsMessage = (
  message: unknown,
): message is IntegrationMessageDeliveredWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as IntegrationMessageDeliveredWsMessage
  if (typed.type !== 'integration_message_delivered') {
    return false
  }

  const payload = typed.data?.payload
  const messageId = Number(payload?.message_id)
  return Number.isFinite(messageId) && messageId > 0 && payload?.status === 'sent'
}

export const matchesIntegrationMessageDeliveredContext = (
  message: IntegrationMessageDeliveredWsMessage,
  context: {
    leadId: number
    conversationId: number | null
    provider?: string
  },
): boolean =>
  isIntegrationDeliveryWsPayloadForContext(message.data.payload, context)

export const isIntegrationMessageNotDeliveredWsMessage = (
  message: unknown,
): message is IntegrationMessageNotDeliveredWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as IntegrationMessageNotDeliveredWsMessage
  if (typed.type !== 'integration_message_not_delivered') {
    return false
  }

  const payload = typed.data?.payload
  const messageId = Number(payload?.message_id)
  const error = payload?.error

  return (
    Number.isFinite(messageId) &&
    messageId > 0 &&
    typeof error === 'string' &&
    error.trim().length > 0
  )
}

export const matchesIntegrationMessageNotDeliveredContext = (
  message: IntegrationMessageNotDeliveredWsMessage,
  context: {
    leadId: number
    conversationId: number | null
    provider?: string
  },
): boolean =>
  isIntegrationDeliveryWsPayloadForContext(message.data.payload, context)

export const isIntegrationMessageDeletedWsMessage = (
  message: unknown,
): message is IntegrationMessageDeletedWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as IntegrationMessageDeletedWsMessage
  if (typed.type !== 'integration_message_deleted') {
    return false
  }

  const payload = typed.data?.payload
  const conversationId = Number(payload?.conversation_id)
  const messageId = Number(payload?.message_id)
  const leadId = Number(payload?.lead_id)

  return (
    Number.isFinite(conversationId) &&
    conversationId > 0 &&
    Number.isFinite(messageId) &&
    messageId > 0 &&
    Number.isFinite(leadId) &&
    leadId > 0
  )
}

export const isIntegrationChannelDeletedWsMessage = (
  message: unknown,
): message is IntegrationChannelDeletedWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as IntegrationChannelDeletedWsMessage
  if (typed.type !== 'integration_channel_deleted') {
    return false
  }

  const payload = typed.data?.payload
  const integrationId = Number(payload?.integration_id)
  const integrationChannelId = Number(payload?.integration_channel_id)

  return (
    Number.isFinite(integrationId) &&
    integrationId > 0 &&
    Number.isFinite(integrationChannelId) &&
    integrationChannelId > 0
  )
}

/** Тихий refetch списка CRM (таблица воронки, канбан по status_ids). */
export const isCrmFunnelListSilentRefetchWsMessage = (
  message: unknown,
): message is CrmFunnelListSilentRefetchWsMessage =>
  isVkMessageInsertedWsMessage(message) ||
  isVkMessageReadWsMessage(message) ||
  isIntegrationMessageReadWsMessage(message) ||
  isLeadCreatedWsMessage(message) ||
  isLeadDuplicateReceivedWsMessage(message)

export const getCrmRealtimeStatusIdsFromWsMessage = (
  message: CrmFunnelListSilentRefetchWsMessage,
): number[] => {
  const payload = message.data?.payload as { status_ids?: unknown }
  return normalizeCrmRealtimeStatusIds(payload?.status_ids)
}

export const isVkMessageNotDeliveredWsMessage = (
  message: unknown,
): message is VkMessageNotDeliveredWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as VkMessageNotDeliveredWsMessage
  if (typed.type !== 'vk_message_not_delivered') {
    return false
  }

  const payload = typed.data?.payload
  const leadId = payload?.lead_id
  const messageId = payload?.message_id
  const error = payload?.error

  return (
    Number.isFinite(Number(leadId)) &&
    Number.isFinite(Number(messageId)) &&
    typeof error === 'string' &&
    error.trim().length > 0
  )
}

export const isVkMessageDeliveredWsMessage = (
  message: unknown,
): message is VkMessageDeliveredWsMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const typed = message as VkMessageDeliveredWsMessage
  if (typed.type !== 'vk_message_delivered') {
    return false
  }

  const leadId = typed.data?.payload?.lead_id
  return Number.isFinite(Number(leadId))
}

export const getVkChatRealtimeEventLeadId = (message: unknown): number | null => {
  if (isVkMessageNotDeliveredWsMessage(message)) {
    return Number(message.data.payload.lead_id)
  }
  if (isVkMessageDeliveredWsMessage(message)) {
    return Number(message.data.payload.lead_id)
  }
  if (isVkMessageInsertedWsMessage(message)) {
    return Number(message.data.payload.lead_id)
  }
  if (isVkMessageReadWsMessage(message)) {
    return Number(message.data.payload.lead_id)
  }
  return null
}

/** Тихий refetch при возврате на вкладку браузера (фон → active). */
export function useRefetchOnBrowserTabVisible(
  onVisible: () => void,
  enabled = true,
): void {
  const onVisibleRef = useRef(onVisible)
  onVisibleRef.current = onVisible

  useEffect(() => {
    if (!enabled) return

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      onVisibleRef.current()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled])
}
